/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import OrderItemsTable from './OrderItemsTable/OrderItemsTable'
import OrderItemsRich from './OrderItemsRich/OrderItemsRich'
import { useImperativeHandle, useRef, useState } from 'react'
import { convertOrderItemsToFulfillmentIssues } from '../../../../services/Order/Order.service'
import { OrderPickingScanProvider } from '../OrderScanPicking'
import { useSaveFulfillment } from './OrderItemFulfillment/useSaveFulfillment'
import { NAVBAR_HEIGHT_IN_PX } from '../../../NavBar'


const orderItemsListDisplayMode = {
    LINES: 1,
    PRODUCTS: 2,
}

const OrderItemsList = ({displayMode, order, orderItems, orderItemListRef}) => {
    const [orderItemsWithFulfillmentIssues, setOrderItemsWithFulfillmentIssues] = useState([])
    const [orderItemsReplacements, setOrderItemsReplacements] = useState([])
    const listDomRef = useRef(null)
    const virtuosoRef = useRef(null)

    useImperativeHandle(orderItemListRef, () => ({
        scrollToItem: (orderItemId) => {
            const mainItems = order.orderItems.filter(oi => !oi.fulfillmentIssueParentId)
            if (displayMode === orderItemsListDisplayMode.PRODUCTS) {
                const index = mainItems.findIndex(oi => oi.id === orderItemId)
                if (index !== -1) {
                    virtuosoRef.current?.scrollToIndex({index, align: 'start', behavior: 'smooth', offset: -NAVBAR_HEIGHT_IN_PX})
                }
            } else {
                let flatIndex = 0
                for (const oi of mainItems) {
                    if (oi.id === orderItemId) break
                    const replacements = order.orderItems.filter(r => r.fulfillmentIssueParentId === oi.id)
                    flatIndex += 1 + replacements.length
                }
                virtuosoRef.current?.scrollToIndex({index: flatIndex, align: 'start', behavior: 'smooth', offset: -NAVBAR_HEIGHT_IN_PX})
            }
        },
        getDomElement: () => listDomRef.current,
    }), [order.orderItems, displayMode])
    const {saveFulfillmentIssuesMutation} = useSaveFulfillment(order.id)

    const clearOrderItemFulfillmentIssue = (orderItem) => {
        const fulfillmentIssuesWithoutOrderItem = orderItemsWithFulfillmentIssues.filter(p => p.id !== orderItem.id)
        setOrderItemsWithFulfillmentIssues(fulfillmentIssuesWithoutOrderItem)

        const fulfillmentIssues = convertOrderItemsToFulfillmentIssues(fulfillmentIssuesWithoutOrderItem, orderItemsReplacements)
        saveFulfillmentIssuesMutation.mutate({fulfillmentIssues})
    }

    const addOrderItemToFulfillmentIssues = (oi, newQuantity, substitution = null) => {
        const getNewOrderFulfillmentIssues = () => {
            const otherItems = orderItemsWithFulfillmentIssues.filter(p => p.id !== oi.id)
            const orderItemToUpdate = {...orderItems.find(orderItem => orderItem.id === oi.id)}
            orderItemToUpdate.quantity = newQuantity
            return otherItems.concat([orderItemToUpdate])
        }

        const newOrderFulfillmentIssues = getNewOrderFulfillmentIssues()

        setOrderItemsWithFulfillmentIssues(newOrderFulfillmentIssues)

        let fulfillmentIssues = convertOrderItemsToFulfillmentIssues(newOrderFulfillmentIssues, orderItemsReplacements)
        if (substitution) {
            const getOrderItemsReplacements = () => {
                const otherItems = orderItemsReplacements.filter(p => p.fulfillmentIssueParentId !== oi.id)
                const orderItemToUpdate = {...orderItems.find(orderItem => orderItem.id === oi.id)}

                const orderItemToCreate = {
                    fulfillmentIssueParentId: orderItemToUpdate.id,
                    name: substitution.name,
                    image: substitution.image,
                    unitPrice: substitution.unitPrice,
                    quantity: substitution.quantity,
                    itemId: substitution.itemId,
                    substitutionId: substitution.substitutionId,
                    scanned: substitution.scanned,
                }

                return otherItems.concat([orderItemToCreate])
            }
            const newOrderItemsReplacements = getOrderItemsReplacements()
            setOrderItemsReplacements(newOrderItemsReplacements)
            fulfillmentIssues = convertOrderItemsToFulfillmentIssues(newOrderFulfillmentIssues, newOrderItemsReplacements)
        }

        saveFulfillmentIssuesMutation.mutate({fulfillmentIssues})
    }

    return (
      <OrderPickingScanProvider
        order={order}
        onFulfillmentIssue={addOrderItemToFulfillmentIssues}
        clearFulfillmentIssue={clearOrderItemFulfillmentIssue}
      >
          <div css={css`margin-top: 10px;`} ref={listDomRef}>
              {displayMode === orderItemsListDisplayMode.PRODUCTS ?
                <OrderItemsRich
                  order={order}
                  hideable
                  virtuosoRef={virtuosoRef}
                />
                :
                <OrderItemsTable
                  order={order}
                  virtuosoRef={virtuosoRef}
                />}
          </div>
      </OrderPickingScanProvider>
    )
}

export { OrderItemsList, orderItemsListDisplayMode }
